import { useEffect, useRef } from 'react';

/**
 * Hook to inject CustomJsHead and CustomJsBody scripts from accommodationGroupDetail API
 * Parses script tags from the HTML string and appends them to head/body
 * Ensures scripts are only injected once per page load
 */
const useCustomScripts = (accommodationGroupDetail) => {
  const injectedRef = useRef(false);

  useEffect(() => {
    if (!accommodationGroupDetail || injectedRef.current) return;

    // CustomJsHead/CustomJsBody are inside accommodationdetailsdb[0]
    const accomDetail = accommodationGroupDetail.accommodationdetailsdb?.[0] || accommodationGroupDetail.accommodationDetails?.[0] || {};
    const customJsHead = accomDetail.CustomJsHead || accomDetail.customJsHead || accommodationGroupDetail.CustomJsHead || accommodationGroupDetail.customJsHead || '';
    const customJsBody = accomDetail.CustomJsBody || accomDetail.customJsBody || accommodationGroupDetail.CustomJsBody || accommodationGroupDetail.customJsBody || '';

    if (!customJsHead && !customJsBody) return;

    injectedRef.current = true;

    const injectScripts = (htmlString, target) => {
      if (!htmlString || !target) return;

      // Parse the HTML string to extract script tags
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlString, 'text/html');
      const scripts = doc.querySelectorAll('script');

      scripts.forEach((scriptEl) => {
        // Skip if script with same id or src already exists
        if (scriptEl.id && document.getElementById(scriptEl.id)) return;
        if (scriptEl.src && document.querySelector(`script[src="${scriptEl.src}"]`)) return;

        const newScript = document.createElement('script');

        // Copy all attributes
        Array.from(scriptEl.attributes).forEach((attr) => {
          newScript.setAttribute(attr.name, attr.value);
        });

        // Copy inline script content
        if (scriptEl.textContent) {
          newScript.textContent = scriptEl.textContent;
        }

        target.appendChild(newScript);
      });

      // Also handle non-script elements (e.g., link, style, meta tags)
      const otherEls = doc.querySelectorAll('link, style, meta');
      otherEls.forEach((el) => {
        const clone = el.cloneNode(true);
        target.appendChild(clone);
      });
    };

    try {
      if (customJsHead) {
        injectScripts(customJsHead, document.head);
      }
      if (customJsBody) {
        injectScripts(customJsBody, document.body);
      }
    } catch (error) {
      // Silently fail - custom scripts should not break the app
    }
  }, [accommodationGroupDetail]);
};

export default useCustomScripts;
